// File: frontend/src/pages/KnowledgeGraph/hooks/useConstellation.ts
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../../../hooks/useApi';
import { createKnowledgeGraphApi, KnowledgeGraphApi } from '../../../services/knowledgeGraphApi';
import {
  KGStatsResponse,
  KGSearchResult,
  KGClusterResponse,
  KGTimelineResponse,
  KGNode,
  MAX_GRAPH_NODES,
} from '../types';

export interface BreadcrumbEntry {
  id: string;
  name: string;
}

export function useConstellation() {
  const { authenticatedFetcher } = useApi();
  const apiRef = useRef<KnowledgeGraphApi | null>(null);

  // Lazily create the API instance (stable across re-renders)
  if (!apiRef.current) {
    apiRef.current = createKnowledgeGraphApi(authenticatedFetcher);
  }
  const api = apiRef.current;

  // --- Stats ---
  const [stats, setStats] = useState<KGStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setStatsLoading(true);
    api.getStats().then((data) => {
      if (!cancelled) {
        setStats(data);
        setStatsLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setStatsLoading(false);
    });
    return () => { cancelled = true; };
  }, [api]);

  // --- Search ---
  const [searchQuery, setSearchQuery] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [searchResults, setSearchResults] = useState<KGSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const doSearch = useCallback(async (query?: string) => {
    const q = query ?? searchQuery;
    if (!q.trim()) return;
    setSearchLoading(true);
    setHasSearched(true);
    try {
      const data = await api.search(q.trim(), entityTypeFilter || undefined);
      setSearchResults(data.results);
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [api, searchQuery, entityTypeFilter]);

  // --- Cluster / Graph ---
  const [cluster, setCluster] = useState<KGClusterResponse | null>(null);
  const [clusterLoading, setClusterLoading] = useState(false);
  const [clusterRootId, setClusterRootId] = useState<string | null>(null);
  const [depth, setDepth] = useState(1);
  const [showInferred, setShowInferred] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [totalNodeCount, setTotalNodeCount] = useState(0);
  const [history, setHistory] = useState<BreadcrumbEntry[]>([]);

  // Overrides let callers pass the value being set in the same event
  // (React state is not updated yet inside the handler).
  const loadCluster = useCallback(async (canonId: string, depthOverride?: number, inferredOverride?: boolean) => {
    setClusterLoading(true);
    try {
      const data = await api.getCluster(canonId, depthOverride ?? depth, inferredOverride ?? showInferred);
      setTotalNodeCount(data.node_count);
      setClusterRootId(canonId);

      // Breadcrumb trail: revisiting an entity truncates the trail back to it
      const rootName = data.nodes.find((n) => n.id === canonId)?.name ?? canonId;
      setHistory((prev) => {
        const idx = prev.findIndex((h) => h.id === canonId);
        if (idx >= 0) return prev.slice(0, idx + 1);
        return [...prev, { id: canonId, name: rootName }];
      });

      // Guard: truncate to MAX_GRAPH_NODES
      if (data.nodes.length > MAX_GRAPH_NODES) {
        setTruncated(true);
        const sorted = [...data.nodes].sort((a, b) => (b.report_count || 0) - (a.report_count || 0));
        const topNodes = sorted.slice(0, MAX_GRAPH_NODES);
        const nodeIds = new Set(topNodes.map((n) => n.id));
        const filteredEdges = data.edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target));
        setCluster({ ...data, nodes: topNodes, edges: filteredEdges, node_count: topNodes.length, edge_count: filteredEdges.length });
      } else {
        setTruncated(false);
        setCluster(data);
      }
    } catch (err) {
      console.error('Cluster load failed:', err);
    } finally {
      setClusterLoading(false);
    }
  }, [api, depth, showInferred]);

  // --- Selected entity ---
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEntity, setSelectedEntity] = useState<KGSearchResult | null>(null);

  const selectEntity = useCallback((entity: KGSearchResult) => {
    setSelectedEntity(entity);
    setSelectedNodeId(entity.id);
    loadCluster(entity.id);
  }, [loadCluster]);

  const selectNodeById = useCallback((nodeId: string | null) => {
    setSelectedNodeId(nodeId);
    if (nodeId && cluster) {
      const node = cluster.nodes.find((n) => n.id === nodeId);
      if (node) {
        setSelectedEntity({
          id: node.id,
          name: node.name,
          type: node.type,
          aliases: node.aliases,
          external_ids: node.external_ids,
          report_count: node.report_count || 0,
        });
      }
    }
  }, [cluster]);

  // Keep the detail panel in sync when the cluster arrives after the
  // selection was made (deep links, breadcrumb jumps, expand).
  useEffect(() => {
    if (!selectedNodeId || !cluster) return;
    if (selectedEntity?.id === selectedNodeId) return;
    const node = cluster.nodes.find((n) => n.id === selectedNodeId);
    if (node) {
      setSelectedEntity({
        id: node.id,
        name: node.name,
        type: node.type,
        aliases: node.aliases,
        external_ids: node.external_ids,
        report_count: node.report_count || 0,
      });
    }
  }, [cluster, selectedNodeId, selectedEntity]);

  // --- Expand (double-click) ---
  const expandNode = useCallback((nodeId: string) => {
    loadCluster(nodeId);
  }, [loadCluster]);

  // --- Entity type visibility (interactive legend filter) ---
  const [hiddenTypes, setHiddenTypes] = useState<string[]>([]);

  const toggleHiddenType = useCallback((type: string) => {
    setHiddenTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  }, []);

  // --- Deep-linking (?entity=...&depth=2&inferred=1) ---
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkDoneRef = useRef(false);

  useEffect(() => {
    if (deepLinkDoneRef.current) return;
    deepLinkDoneRef.current = true;
    const entity = searchParams.get('entity');
    if (!entity) return;
    const d = Math.min(3, Math.max(1, parseInt(searchParams.get('depth') || '1', 10) || 1));
    const inferred = searchParams.get('inferred') === '1';
    setDepth(d);
    setShowInferred(inferred);
    setSelectedNodeId(entity);
    loadCluster(entity, d, inferred);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!clusterRootId) return;
    const params = new URLSearchParams();
    params.set('entity', clusterRootId);
    if (depth !== 1) params.set('depth', String(depth));
    if (showInferred) params.set('inferred', '1');
    setSearchParams(params, { replace: true });
  }, [clusterRootId, depth, showInferred, setSearchParams]);

  // --- Timeline ---
  const [timeline, setTimeline] = useState<KGTimelineResponse | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  useEffect(() => {
    if (!selectedNodeId) {
      setTimeline(null);
      return;
    }
    let cancelled = false;
    setTimelineLoading(true);
    api.getTimeline(selectedNodeId).then((data) => {
      if (!cancelled) {
        setTimeline(data);
        setTimelineLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setTimelineLoading(false);
    });
    return () => { cancelled = true; };
  }, [api, selectedNodeId]);

  // --- Related nodes (derived from cluster) ---
  const relatedNodes: KGNode[] = useMemo(() => {
    if (!cluster || !selectedNodeId) return [];
    return cluster.nodes.filter((n) => n.id !== selectedNodeId);
  }, [cluster, selectedNodeId]);

  // --- Real degree of the selected node within the loaded cluster ---
  // (relatedNodes.length is the size of the whole view, not the node's connections)
  const selectedConnectionCount = useMemo(() => {
    if (!cluster || !selectedNodeId) return 0;
    const neighbors = new Set<string>();
    cluster.edges.forEach((e) => {
      if (e.source === selectedNodeId) neighbors.add(e.target);
      if (e.target === selectedNodeId) neighbors.add(e.source);
    });
    return neighbors.size;
  }, [cluster, selectedNodeId]);

  // --- Detail panel collapse ---
  const [detailCollapsed, setDetailCollapsed] = useState(false);

  // --- Example query handler ---
  const handleExampleQuery = useCallback((term: string) => {
    setSearchQuery(term);
    // Trigger search immediately
    setSearchLoading(true);
    setHasSearched(true);
    api.search(term).then((data) => {
      setSearchResults(data.results);
      setSearchLoading(false);
    }).catch(() => {
      setSearchResults([]);
      setSearchLoading(false);
    });
  }, [api]);

  return {
    // Stats
    stats,
    statsLoading,
    // Search
    searchQuery,
    setSearchQuery,
    entityTypeFilter,
    setEntityTypeFilter,
    doSearch,
    searchResults,
    searchLoading,
    hasSearched,
    // Graph
    cluster,
    clusterLoading,
    clusterRootId,
    depth,
    setDepth,
    showInferred,
    setShowInferred,
    truncated,
    totalNodeCount,
    loadCluster,
    history,
    // Type filter
    hiddenTypes,
    toggleHiddenType,
    // Selection
    selectedNodeId,
    selectedEntity,
    selectEntity,
    selectNodeById,
    expandNode,
    // Timeline
    timeline,
    timelineLoading,
    // Related
    relatedNodes,
    selectedConnectionCount,
    // Detail panel
    detailCollapsed,
    setDetailCollapsed,
    // Examples
    handleExampleQuery,
  };
}
